import { match } from "ts-pattern";
import type { Simplify } from "type-fest";

import {
  categoriesOf,
  commentsRequired,
  problemsIn,
  scaleOf,
  type AssessmentProgress,
  type FormControl,
  type GroupCategory,
  type GroupProgress,
  type GroupState,
  type JsonValue,
  type RatingScale,
} from "@thalamos/haven-common";

import {
  answerOf,
  commentProblemsAt,
  type ItemElement,
} from "../../components/AssessmentForm/answering.js";
import type { OpenedForm } from "./AssessmentContext.js";

/** Where an item's comment stands: `not_required` unless the rules require one for the answer given. */
export type ItemCommentState = "not_required" | "missing" | "written";

/** An answer as the overview shows it: a number on its scale, or words: an option's title, such as not applicable, or the response as written. */
export type OverviewAnswer =
  | { kind: "rated"; rating: number; scale: RatingScale }
  | { kind: "worded"; text: string };

export type OverviewItem = {
  readonly item: string;
  readonly title: string;
  /** Undefined until the item is answered. */
  readonly answer: OverviewAnswer | undefined;
  /** Answered, with no problem left at the item or its comment. */
  readonly done: boolean;
  readonly comment: ItemCommentState;
};

/** A domain as the overview lists it: its progress, and each item the form asks of it in the form's order. */
export type OverviewDomain = Simplify<
  Pick<GroupProgress, "group" | "state" | "done" | "total"> & {
    readonly title: string;
    /** Undefined for an instrument that declares no categories. */
    readonly category: GroupCategory | undefined;
    /** How many answered items lack the comment the rules require for their answer. */
    readonly missingComments: number;
    readonly items: readonly OverviewItem[];
  }
>;

type ChoiceOption = Extract<FormControl, { kind: "choice" }>["options"][number];

/** How a response that is not a rating reads: its option's title where it is one, otherwise as it was written. */
export const answerWordingOf = ({
  options,
  response,
}: {
  options: readonly ChoiceOption[];
  response: JsonValue;
}): string =>
  options.find(({ value }) => value === response)?.title.text ??
  (typeof response === "string" ? response : JSON.stringify(response));

const answerShownOf = (
  element: ItemElement,
  response: JsonValue,
): OverviewAnswer => {
  const { control } = element.presentation;
  const options = control.kind === "choice" ? control.options : [];

  return typeof response === "number"
    ? {
        kind: "rated",
        rating: response,
        scale: scaleOf(options.map(({ value }) => value)),
      }
    : { kind: "worded", text: answerWordingOf({ options, response }) };
};

/**
 * Each domain of an opened form as the overview lists it, from its progress.
 * A comment is missing where the form's own checks report it so, and written
 * where one is required and they report nothing.
 */
export const overviewDomainsOf = ({
  opened,
  progress,
}: {
  opened: OpenedForm;
  progress: AssessmentProgress;
}): readonly OverviewDomain[] => {
  const { schema, form, draft } = opened;
  const problems = problemsIn({
    schema,
    form,
    document: draft.document,
    context: draft.context,
  }).unwrapOr([]);
  const required = commentsRequired({
    schema,
    form,
    document: draft.document,
    context: draft.context,
  }).unwrapOr(new Set<string>());
  const categories = categoriesOf(schema);

  return progress.groups.map(({ group, state, done, total, items }) => {
    const overviewItems = items.flatMap(
      ({ item, answered, done: itemDone }): OverviewItem[] => {
        const element = form.find(
          (each): each is ItemElement =>
            each.kind === "item" &&
            each.at.group === group &&
            each.at.item === item,
        );

        if (element === undefined) {
          return [];
        }

        const answer = answered ? answerOf(draft.document, element) : undefined;
        const commentMissing = commentProblemsAt(problems, element).some(
          ({ code }) => code === "must-have-a-comment",
        );
        const comment: ItemCommentState = commentMissing
          ? "missing"
          : answer !== undefined && required.has(item)
            ? "written"
            : "not_required";

        return [
          {
            item,
            title: element.presentation.title.text,
            answer:
              answer === undefined
                ? undefined
                : answerShownOf(element, answer.response),
            done: itemDone,
            comment,
          },
        ];
      },
    );

    return {
      group,
      title:
        form.find(
          (element) => element.kind === "group" && element.at.group === group,
        )?.presentation.title.text ?? group,
      category: categories?.get(group),
      state,
      done,
      total,
      missingComments: overviewItems.filter(
        ({ comment }) => comment === "missing",
      ).length,
      items: overviewItems,
    };
  });
};

/** How many required items are done, out of those required, across the domains that count towards readiness. */
export const requiredItemsDoneOf = (
  progress: AssessmentProgress,
): Pick<GroupProgress, "done" | "total"> =>
  progress.groups
    .filter(({ included }) => included)
    .reduce(
      (sum, { done, total }) => ({
        done: sum.done + done,
        total: sum.total + total,
      }),
      { done: 0, total: 0 },
    );

/** What the overview offers for a domain, following where it stands. */
export type DomainAction = "start" | "continue" | "review";

export const domainActionOf = (state: GroupState): DomainAction =>
  match(state)
    .with("not_opened", "not_started", (): DomainAction => "start")
    .with("in_progress", (): DomainAction => "continue")
    .with("completed", (): DomainAction => "review")
    .exhaustive();
